import { useContext, useEffect, useRef } from 'react'
import { generatePath, useParams } from 'react-router'
import { Anchor, Button, Container, Group, Stack } from '@mantine/core'
import { PageContainer } from '@shared/components/app-shell/page-container/page-container'
import { PAGE_TITLES } from '@/constants/page-titles'
import { showErrorNotification } from '@shared/helpers/notifications/notifications'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { GetSpeciStationDataGql } from '@/graphql/queries/speci/get-speci-station-data.graphql'
import { GetSpeciGql } from '@/graphql/queries/speci/get-speci.graphql'
import { StationContext } from '@/pages/station-context'
import { ViewWoolSpecification } from 'shared/src/components/speci/view-wool-specification/view-wool-specification'
import { Link } from 'react-router-dom'
import { IconPrinter } from '@tabler/icons-react'
import { useReactToPrint } from 'react-to-print'
import { PrintOnly } from '@shared/components/print-only/print-only'
import { useNavigation } from '@/hooks/use-navigation'
import { routes } from '@/routing/routes'

export function ViewWoolSpecificationPage() {
  const { station } = useContext(StationContext)
  const { speciId } = useParams()
  const { navigateToListSpeci, navigateToHome } = useNavigation()

  const navToSpeciHomepage = () => {
    navigateToListSpeci(station.recordNumber)
  }

  useEffect(() => {
    // If no station is selected, navigate to the station page
    if (!station) {
      showErrorNotification('Please select a station first', { autoClose: true })
      navigateToHome()
    }
  }, [station])

  useEffect(() => {
    // If no speciId is present in the URL, navigate to the speci homepage
    if (!speciId) {
      navToSpeciHomepage()
    }
  }, [speciId])

  // Fetch additional station data
  const { data: stationData, isLoading: stationDataLoading } = useAuthenticatedGraphQuery({
    queryKey: ['speciStationDetails', station.recordNumber],
    gql: GetSpeciStationDataGql,
    queryParams: { recordNumber: station.recordNumber },
    enabled: !!station.recordNumber,
  })

  const { data: speciData } = useAuthenticatedGraphQuery({
    queryKey: ['getSpeci', speciId],
    gql: GetSpeciGql,
    queryParams: { speciId },
    enabled: !!speciId,
  })

  const speci = speciData?.speci?.speci

  // Print table functionality
  const printComponentRef = useRef<HTMLDivElement>(null)
  const handlePrintButtonClick = useReactToPrint({
    contentRef: printComponentRef,
  })
  const printSpeci = () => {
    try {
      handlePrintButtonClick()
    } catch (error) {
      showErrorNotification('Printing failed. Please try again.')
    }
  }

  return (
    <PageContainer title={PAGE_TITLES.WOOL_SPECIFICATIONS}>
      <Container size='xl'>
        <Stack>
          <Group>
            <Anchor
              component={Link}
              size='sm'
              to={generatePath(routes.listSpeci.path, {
                stationRecordNumber: station.recordNumber,
              })}
            >
              ⟵ Back to Wool Specifications
            </Anchor>
          </Group>
          <Group justify='flex-end'>
            <Button onClick={printSpeci} leftSection={<IconPrinter />} disabled={!speci}>
              Print speci
            </Button>
          </Group>
          <div data-testid='print-wool-specification'>
            <PrintOnly ref={printComponentRef}>
              <ViewWoolSpecification
                stationDetailsForSpeci={stationData?.stationDetailsForSpeci}
                stationDataLoading={stationDataLoading}
                speci={speci}
                stationRecordNumber={station.recordNumber}
                displayLogo
              />
            </PrintOnly>
          </div>
          <div data-testid='view-wool-specification'>
            <ViewWoolSpecification
              stationDetailsForSpeci={stationData?.stationDetailsForSpeci}
              stationDataLoading={stationDataLoading}
              speci={speci}
              stationRecordNumber={station.recordNumber}
            />
          </div>
          <Group justify='flex-end'>
            <Anchor
              component={Link}
              size='sm'
              to={generatePath(routes.listSpeci.path, {
                stationRecordNumber: station.recordNumber,
              })}
            >
              ⟵ Back to Wool Specifications
            </Anchor>
          </Group>
        </Stack>
      </Container>
    </PageContainer>
  )
}
